import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import Card from '../common/Card';
import Table from '../common/Table';
import Modal from '../common/Modal';
import './Users.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Users = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '', status: '' });

  const getToken = () => localStorage.getItem('marilink_token');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/admin/users/${selectedUser._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });
      const data = await response.json();
      if (data.success) {
        alert('User updated successfully');
        fetchUsers();
        setShowEditModal(false);
      } else {
        alert(data.message);
      }
    } catch (error) {
      alert('Error updating user: ' + error.message);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) || user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || (user.isActive ? 'active' : 'suspended') === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const stats = {
    total: users.length,
    owners: users.filter(u => u.role === 'owner').length,
    vendors: users.filter(u => u.role === 'service-provider').length,
    admins: users.filter(u => u.role === 'admin').length,
    active: users.filter(u => u.isActive).length
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'email', label: 'Email' },
    { key: 'company', label: 'Company' },
    { key: 'role', label: 'Role', render: (val) => <span className={`role-badge role-${val}`}>{val}</span> },
    { key: 'createdAt', label: 'Joined', render: (val) => new Date(val).toLocaleDateString() },
    { key: 'isActive', label: 'Status', render: (val) => <span className={`status-badge ${val ? 'active' : 'suspended'}`}>{val ? 'Active' : 'Suspended'}</span> }
  ];

  const actions = [
    { icon: 'fa-eye', label: 'View', onClick: (row) => navigate(`/admin/user/${row._id}`) },
    { icon: 'fa-edit', label: 'Edit', onClick: (row) => { setSelectedUser(row); setEditForm({ name: row.name, email: row.email, role: row.role, status: row.isActive ? 'active' : 'suspended' }); setShowEditModal(true); } }
  ];

  if (loading) return <div className="loading-spinner">Loading users...</div>;

  return (
    <div className="admin-users-page">
      <div className="page-header">
        <h1><i className="fas fa-users"></i> User Management</h1>
        <p>Manage platform users and permissions</p>
      </div>

      <div className="stats-grid">
        <Card title="Total Users" value={stats.total} icon="fa-users" />
        <Card title="Ship Owners" value={stats.owners} icon="fa-ship" color="blue" />
        <Card title="Service Providers" value={stats.vendors} icon="fa-building" color="green" />
        <Card title="Active Users" value={stats.active} icon="fa-check-circle" color="success" />
      </div>

      <div className="filters-bar">
        <div className="filter-group">
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="filter-select">
            <option value="all">All Roles</option><option value="owner">Owner</option><option value="service-provider">Service Provider</option><option value="admin">Admin</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
            <option value="all">All Status</option><option value="active">Active</option><option value="suspended">Suspended</option>
          </select>
        </div>
        <div className="search-box"><i className="fas fa-search"></i><input type="text" placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
      </div>

      <Table columns={columns} data={filteredUsers} actions={actions} emptyMessage="No users found" />

      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit User">
        {selectedUser && (<><div className="form-group"><label>Name</label><input type="text" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} /></div>
        <div className="form-group"><label>Email</label><input type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} /></div>
        <div className="form-group"><label>Role</label><select value={editForm.role} onChange={(e) => setEditForm({...editForm, role: e.target.value})}><option value="owner">Owner</option><option value="service-provider">Service Provider</option><option value="admin">Admin</option></select></div>
        <div className="form-group"><label>Status</label><select value={editForm.status} onChange={(e) => setEditForm({...editForm, status: e.target.value})}><option value="active">Active</option><option value="suspended">Suspended</option></select></div>
        <div className="modal-actions"><Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button><Button variant="primary" onClick={handleEdit}>Save Changes</Button></div></>)}
      </Modal>
    </div>
  );
};

export default Users;